import React, { useLayoutEffect, useRef, useState } from "react"

import { ArrowUp, Clock } from "lucide-react"

import { segmentFitsLabel } from "@/components/shared/statBandLayout"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { VARIANCE_LABELS } from "@/lib/statusDisplay"
import {
  type Tone,
  type Variance,
  VARIANCE_DISPLAY_ORDER,
  toneClasses,
  varianceClasses,
} from "@/lib/tone"
import { cn } from "@/lib/utils"

/** Summary band for monitoring and planning. Every figure covers the whole day, never a sample. */

export interface StatChip {
  key: string
  icon?: React.ReactNode
  count?: number
  label: string
  hint?: string
  tone: Tone
  active?: boolean
  onClick?: () => void
}

export interface StatMetric {
  key: string
  icon?: React.ReactNode
  label: string
  value: string
  tone?: Tone
}

export interface VarianceCounts {
  counts: Record<Variance, number>
  /** Cancelled routes are excluded — they have no variance from a plan. */
  total: number
  ahead: number
  notStarted: number
  cancelled: number
  breakdown?: Partial<Record<Variance, string[]>>
  /** Composition of the alarm routes, each counted once so it sums to the strip's figure. */
  alarmCauses?: string[]
}

export interface StatBandProps {
  lede?: string
  variance?: VarianceCounts
  /** Expected to filter the list below to the routes the strip counts. */
  onShowAlarms?: () => void
  /** Must be shown, not just tracked: a filtered board otherwise looks like a quiet one. */
  alarmsShown?: boolean
  chips?: StatChip[]
  metrics?: StatMetric[]
  className?: string
}

type AlarmVariance = Extract<Variance, "at_risk" | "late">

const ALARM_BANDS: AlarmVariance[] = ["at_risk", "late"]

/** Shape as well as colour, so the strip still ranks correctly in greyscale. */
const VARIANCE_GLYPH: Record<AlarmVariance, string> = {
  late: "▲",
  at_risk: "■",
}

const BREAKDOWN_LIMIT = 8

function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.getBoundingClientRect().width)
    // Absent in jsdom; the legend covers the missing in-bar counts.
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return [ref, width] as const
}

function BandTooltip({
  band,
  count,
  lines,
  children,
}: {
  band: Variance
  count: number
  lines?: string[]
  children: React.ReactNode
}) {
  if (!lines || lines.length === 0) return <>{children}</>

  const head = lines.slice(0, BREAKDOWN_LIMIT)
  const rest = lines.length - head.length

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-sm">
        <p className="mb-1 text-xs font-semibold">
          {count} {VARIANCE_LABELS[band]}
        </p>
        <ul className="flex flex-col gap-0.5 text-xs">
          {head.map((line) => (
            <li key={line} className="whitespace-nowrap tabular-nums">
              {line}
            </li>
          ))}
          {rest > 0 && <li className="opacity-70">and {rest} more</li>}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}

function VarianceBar({ variance }: { variance: VarianceCounts }) {
  const present = VARIANCE_DISPLAY_ORDER.filter((v) => variance.counts[v] > 0)
  const summary = present.map((v) => `${variance.counts[v]} ${VARIANCE_LABELS[v]}`).join(", ")
  const lines = (v: Variance) => variance.breakdown?.[v]
  const [barRef, barWidth] = useMeasuredWidth<HTMLDivElement>()

  return (
    <>
      {/* role="img" makes segment tooltips mouse-only; the legend serves keyboards. */}
      <div
        ref={barRef}
        className="flex h-9 w-full overflow-hidden rounded-md border border-palette-jungleGreen200"
        role="img"
        aria-label={summary || "No routes"}
      >
        {present.map((v) => {
          const share = (variance.counts[v] / variance.total) * 100
          return (
            <BandTooltip key={v} band={v} count={variance.counts[v]} lines={lines(v)}>
              <div
                className={cn(
                  "flex h-full items-center justify-center",
                  varianceClasses(v).fill,
                  varianceClasses(v).onFill,
                )}
                style={{ width: `${share}%` }}
              >
                {segmentFitsLabel(share, barWidth) && (
                  <span className="text-sm font-semibold tabular-nums">{variance.counts[v]}</span>
                )}
              </div>
            </BandTooltip>
          )
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        {present.map((v) => {
          const label = (
            <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
              <span
                className={cn(
                  "h-2.5 w-2.5 flex-none rounded-sm border border-palette-jungleGreen300",
                  varianceClasses(v).fill,
                )}
                aria-hidden="true"
              />
              <span className="font-semibold tabular-nums text-palette-jungleGreen900">
                {variance.counts[v]}
              </span>{" "}
              {/* Explicit space: otherwise the accessible name reads "4late". */}
              <span className="text-palette-jungleGreen500">{VARIANCE_LABELS[v]}</span>
            </span>
          )

          return lines(v) ? (
            <BandTooltip key={v} band={v} count={variance.counts[v]} lines={lines(v)}>
              <button
                type="button"
                className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palette-jungleGreen500"
              >
                {label}
              </button>
            </BandTooltip>
          ) : (
            <React.Fragment key={v}>{label}</React.Fragment>
          )
        })}
      </div>
    </>
  )
}

function Chip({ chip }: { chip: StatChip }) {
  const t = toneClasses(chip.tone)
  const classes = cn(
    "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors",
    t.badge,
    chip.onClick && "hover:brightness-[0.97] focus-visible:outline-none focus-visible:ring-2",
    chip.active && "ring-2 ring-offset-1 ring-palette-jungleGreen500",
  )

  const body = (
    <>
      {chip.icon}
      {/* Explicit spaces: otherwise the accessible name reads "7late". */}
      <span>
        {chip.count !== undefined && (
          <>
            <span className="font-semibold tabular-nums">{chip.count}</span>{" "}
          </>
        )}
        {chip.label}
        {chip.hint && <span className="font-normal opacity-70"> {chip.hint}</span>}
      </span>
    </>
  )

  if (!chip.onClick) {
    return <span className={classes}>{body}</span>
  }

  return (
    <button type="button" className={classes} aria-pressed={!!chip.active} onClick={chip.onClick}>
      {body}
    </button>
  )
}

export function StatBand({
  lede,
  variance,
  onShowAlarms,
  alarmsShown = false,
  chips,
  metrics,
  className,
}: StatBandProps) {
  const hasBar = !!variance && variance.total > 0

  const alarmCount = variance
    ? ALARM_BANDS.reduce((sum, band) => sum + variance.counts[band], 0)
    : 0

  const alarmStates = variance
    ? ALARM_BANDS.filter((band) => variance.counts[band] > 0).map((band) => VARIANCE_LABELS[band])
    : []
  const alarmLabel = `${alarmCount} ${alarmCount === 1 ? "route" : "routes"} ${alarmStates.join(" or ")}`
  const worst = variance
    ? ([...ALARM_BANDS].reverse().find((band) => variance.counts[band] > 0) ?? null)
    : null

  return (
    <div
      className={cn("flex-shrink-0 border-b border-palette-jungleGreen200 bg-white", className)}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-6 pt-2.5 pb-1.5">
        {lede && (
          <span className="whitespace-nowrap text-sm font-semibold text-palette-jungleGreen900">
            {lede}
          </span>
        )}
        {!!variance?.notStarted && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-palette-skyBlue800">
            <Clock size={12} aria-hidden="true" />
            <span className="font-semibold tabular-nums">{variance.notStarted}</span> not started
          </span>
        )}
        {!!variance?.ahead && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-variance-ahead">
            <ArrowUp size={12} aria-hidden="true" />
            <span className="font-semibold tabular-nums">{variance.ahead}</span> ahead
          </span>
        )}
        {metrics?.map((m) => (
          <span key={m.key} className="inline-flex items-center gap-1.5 whitespace-nowrap">
            {m.icon}
            <span className="text-xs text-palette-jungleGreen500">{m.label}</span>
            <span
              className={cn(
                "text-sm font-semibold tabular-nums",
                m.tone ? toneClasses(m.tone).text : "text-palette-jungleGreen900",
              )}
            >
              {m.value}
            </span>
          </span>
        ))}
        {chips?.map((chip) => (
          <Chip key={chip.key} chip={chip} />
        ))}
      </div>

      {hasBar && (
        <div className="flex flex-col gap-1.5 px-6 pb-2.5">
          <VarianceBar variance={variance} />
        </div>
      )}

      {/* Shown only when something needs a human, in the worst band's colour. */}
      {worst && alarmCount > 0 && (
        <button
          type="button"
          onClick={onShowAlarms}
          disabled={!onShowAlarms}
          aria-pressed={onShowAlarms ? alarmsShown : undefined}
          className={cn(
            "flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 px-6 py-2 text-left transition-[filter]",
            varianceClasses(worst).strip ?? varianceClasses(worst).fill,
            "text-white",
            onShowAlarms && "hover:brightness-110",
            alarmsShown && "ring-2 ring-inset ring-white/70",
          )}
        >
          <span className="text-sm leading-none" aria-hidden="true">
            {VARIANCE_GLYPH[worst]}
          </span>
          <span className="text-sm font-semibold">{alarmLabel}</span>
          {variance?.alarmCauses?.length ? (
            <span className="text-xs opacity-90">{variance.alarmCauses.join(" · ")}</span>
          ) : null}
          {onShowAlarms && (
            <span className="ml-auto text-xs font-medium underline underline-offset-2">
              {alarmsShown ? "Showing only these — show all" : "Show them"}
            </span>
          )}
        </button>
      )}
    </div>
  )
}

export default StatBand
